// src/admin/AdminPromotions.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminPromotions() {
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState([]);
  const [discount, setDiscount]   = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [message, setMessage]     = useState(null);
  const [saving, setSaving]       = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  /* =========================================================
     CHARGER PRODUITS + PROMOTIONS
  ========================================================= */
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://madadline.com/api/products-with-promotions');
      console.log('🎯 Produits avec promos:', data);
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Erreur:', err);
      try {
        const { data } = await axios.get('https://madadline.com/api/products');
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  /* =========================================================
     SÉLECTION
  ========================================================= */
  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelected(products.map(p => p.id));
  const clearSelection = () => setSelected([]);

  /* =========================================================
     SUPPRIMER UNE PROMOTION
  ========================================================= */
  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `https://madadline.com/api/promotions/${promoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: '✓ Promotion supprimée' });
      fetchProducts();
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erreur' });
    }
  };

  /* =========================================================
     SOUMISSION
  ========================================================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (selected.length === 0)  return setMessage({ type: 'error', text: 'Sélectionnez au moins un produit.' });
    if (!discount || discount < 1 || discount > 100) return setMessage({ type: 'error', text: 'Réduction entre 1 et 100.' });
    if (!startDate || !endDate) return setMessage({ type: 'error', text: 'Renseignez les dates.' });
    if (new Date(startDate) > new Date(endDate)) return setMessage({ type: 'error', text: 'Date de fin doit être après le début.' });

    setSaving(true);

    try {
      const token = localStorage.getItem('auth_token');
      let success = 0;
      let errors = [];

      for (const product_id of selected) {
        try {
          await axios.post(
            'https://madadline.com/api/promotions',
            {
              product_id,
              discount:   Number(discount),
              start_date: startDate,
              end_date:   endDate,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          success++;
        } catch (err) {
          console.error(`Erreur produit ${product_id}:`, err.response?.data || err);
          errors.push(err.response?.data?.message || 'Erreur');
        }
      }

      if (success > 0) {
        setMessage({
          type: 'success',
          text: `✓ ${success} promotion(s) appliquée(s)${errors.length > 0 ? ` (${errors.length} échec)` : ''}`,
        });
        setSelected([]);
        setDiscount('');
        setStartDate('');
        setEndDate('');
        await fetchProducts();
      } else {
        setMessage({ type: 'error', text: 'Aucune promotion appliquée.' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: "Erreur lors de l'application." });
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     STATS
  ========================================================= */
  const activePromos = products.filter(p => p.promotion?.is_active === true).length;
  const expiredPromos = products.filter(p => p.promotion && p.promotion.is_active === false).length;

  /* =========================================================
     CALCUL PRIX PROMO
  ========================================================= */
  const getPromoPrice = (price, discount) => {
    return Math.round(Number(price) * (1 - Number(discount) / 100) * 100) / 100;
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-promotions">

      <header className="admin-page-head">
        <div>
          <h1>Promotions</h1>
          <p>
            <span style={{ color: '#16A34A', fontWeight: '600' }}>{activePromos} active{activePromos > 1 ? 's' : ''}</span>
            {' · '}
            <span style={{ color: '#888' }}>{expiredPromos} expirée{expiredPromos > 1 ? 's' : ''}</span>
            {' · '}
            {products.length} produit{products.length > 1 ? 's' : ''}
          </p>
        </div>
      </header>

      {/* FORMULAIRE */}
      <div className="admin-card">
        <div className="admin-card__head">
          <h2>Nouvelle promotion</h2>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form__grid">
            <div className="admin-form__group">
              <label>Réduction (%) *</label>
              <input
                type="number"
                value={discount}
                onChange={e => setDiscount(e.target.value)}
                min="1"
                max="100"
                placeholder="20"
              />
            </div>
            <div className="admin-form__group">
              <label>Date de début *</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                min={today}
              />
            </div>
            <div className="admin-form__group">
              <label>Date de fin *</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate || today}
              />
            </div>
          </div>

          {message && (
            <div className={`admin-alert admin-alert--${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          <div className="admin-form__actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="admin-btn admin-btn--primary"
              disabled={saving || selected.length === 0}
            >
              {saving ? 'Application…' : `Appliquer à ${selected.length} produit(s)`}
            </button>
            <button type="button" className="admin-btn" onClick={selectAll} disabled={saving}>
              Tout sélectionner
            </button>
            {selected.length > 0 && (
              <button type="button" className="admin-btn" onClick={clearSelection} disabled={saving}>
                Désélectionner ({selected.length})
              </button>
            )}
          </div>
        </form>
      </div>

      {/* GRILLE PRODUITS */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : products.length === 0 ? (
        <div className="admin-empty">
          <p>Aucun produit disponible.</p>
        </div>
      ) : (
        <div className="admin-promo-grid">
          {products.map(p => {
            const isSelected = selected.includes(p.id);
            const promo = p.promotion;
            const isActive = promo?.is_active === true;
            const originalPrice = Number(p.prix || 0);
            const promoPrice = promo ? getPromoPrice(originalPrice, promo.discount) : null;

            return (
              <div
                key={p.id}
                className={`admin-promo-card ${isSelected ? 'is-selected' : ''}`}
                onClick={() => toggle(p.id)}
                style={{ position: 'relative' }}
              >
                {/* Checkbox */}
                <div className="admin-promo-card__check">
                  {isSelected && '✓'}
                </div>

                {/* Badge promo */}
                {promo && (
                  <div
                    className="admin-promo-card__badge"
                    style={{
                      background: isActive ? '#DC2626' : '#6B7280',
                      fontSize: '11px',
                    }}
                  >
                    -{promo.discount}%
                    {!isActive && (
                      <span style={{ fontSize: '9px', marginLeft: '2px' }}>exp.</span>
                    )}
                  </div>
                )}

                {/* Image */}
                <img
                  src={p.image?.startsWith('http') ? p.image : `https://madadline.com/${p.image}`}
                  alt={p.nom}
                />

                {/* Infos */}
                <div className="admin-promo-card__info">
                  <strong>{p.nom}</strong>

                  {/* ⭐ NOUVEAU : prix avec promo */}
                  {promo ? (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '15px',
                          fontWeight: '700',
                          color: isActive ? '#DC2626' : '#888',
                        }}
                      >
                        {promoPrice?.toFixed(0)} DH
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#aaa',
                          textDecoration: 'line-through',
                        }}
                      >
                        {originalPrice.toFixed(0)} DH
                      </span>
                    </div>
                  ) : (
                    <span>{originalPrice.toFixed(0)} DH</span>
                  )}

                  {/* Dates */}
                  {promo && (
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>
                      {promo.start_date} → {promo.end_date}
                    </div>
                  )}

                  {/* Statut */}
                  {promo && (
                    <div style={{
                      fontSize: '10px',
                      marginTop: '2px',
                      color: isActive ? '#16A34A' : '#DC2626',
                      fontWeight: '600',
                    }}>
                      {isActive ? '● Active' : '● Expirée'}
                    </div>
                  )}
                </div>

                {/* Bouton supprimer */}
                {promo && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePromo(promo.id);
                    }}
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ Retirer
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminPromotions;